/**
 * 学生档案 API（student-registry change 2-4）。
 *
 * 对齐后端 student-registry REST 契约（11 端点）：
 * - GET /students -> Paginated<StudentListItem>（筛选 + 分页 + 手机号脱敏）
 * - GET /students/:id -> StudentDetail（脱敏）
 * - POST /students -> StudentDetail（生成学号）
 * - PATCH /students/:id -> StudentDetail（只读保护 + 手机号变更事件）
 * - POST /students/:id/archive -> {id,status}（幂等）
 * - POST /students/:id/restore -> {id,status}（幂等）
 * - POST /students/import/preview -> StudentImportPreview（不落库）
 * - POST /students/import -> StudentImportResult（幂等）
 * - GET /students/search -> StudentSearchItem[]（联想，脱敏）
 * - GET /students/:id/mobile -> string（明文，权限 + 审计）
 * - GET /students/by-mobile -> StudentSummary[]（内部端口透出）
 *
 * 路径从 api.config.ts 导入；Transport 统一解包 `{success, data}` 信封。
 */
import { ApiPaths } from './api.config';
import { http } from './transport';

/** 学生性别。 */
export type StudentGender = 'MALE' | 'FEMALE';
/** 学段 5 值权威枚举（design D3）。 */
export type GradeStage =
  | 'PRESCHOOL'
  | 'PRIMARY_LOW'
  | 'PRIMARY_HIGH'
  | 'JUNIOR'
  | 'SENIOR';
/** 学生存储状态（一期展示 2 值）。 */
export type StudentStatus = 'STUDYING' | 'ARCHIVED';
/** 监护人关系。 */
export type GuardianRelationship =
  | 'FATHER'
  | 'MOTHER'
  | 'GRANDPA'
  | 'GRANDMA'
  | 'OTHER';

/** 分享来源页（6 值）。 */
export type ShareSourcePage =
  | 'HOME'
  | 'PRODUCT_LIST'
  | 'PRODUCT_DETAIL'
  | 'SCHEDULE'
  | 'ENTITLEMENT'
  | 'PROFILE';

/** 学生列表项（手机号脱敏）。 */
export interface StudentListItem {
  id: string;
  studentNo: string;
  name: string;
  gender: StudentGender;
  gradeStage: GradeStage;
  homeCampusId: string;
  homeCampusName?: string;
  /** 监护人手机号（脱敏，中间四位掩码） */
  guardianMobile: string;
  status: StudentStatus;
  updatedAt: string;
}

/** 监护人联系人响应项。 */
export interface GuardianItem {
  id: string;
  name: string;
  relationship: GuardianRelationship;
}

/** 学生科目分数项（student_subject_scores 一行，按科目名排序）。 */
export interface SubjectScore {
  subjectId: string;
  subjectName: string;
  score: number;
}

/** 学生详情（手机号脱敏）。 */
export interface StudentDetail {
  id: string;
  studentNo: string;
  homeCampusId: string;
  homeCampusName?: string;
  name: string;
  gender: StudentGender;
  gradeStage: GradeStage;
  tags?: string[];
  /** 学生各科目当前分数（无分数为空数组）。 */
  subjectScores: SubjectScore[];
  guardianMobile: string;
  guardianMobileChangedAt?: string | null;
  status: StudentStatus;
  archivedAt?: string | null;
  referrerId?: string | null;
  shareSourcePage?: ShareSourcePage | null;
  shareProductId?: string | null;
  /** 介绍人显示名称快照（归因时锁定）。 */
  referrerDisplayName?: string | null;
  /** 介绍人注册时间快照（归因时锁定）。 */
  referrerRegisteredAt?: string | null;
  /** 分享商品名称快照（归因时锁定）。 */
  shareProductName?: string | null;
  createdAt: string;
  guardians: GuardianItem[];
  subjectIds?: string[];
}

/** 学生联想项（脱敏）。 */
export interface StudentSearchItem {
  id: string;
  studentNo: string;
  name: string;
  guardianMobile: string;
  homeCampusName?: string;
  status: StudentStatus;
}

/** 分页响应。 */
export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

/** 学生列表查询参数。 */
export interface StudentListQuery {
  keyword?: string;
  campus?: string;
  status?: StudentStatus;
  gradeStage?: GradeStage;
  gender?: StudentGender;
  scoreMin?: number;
  scoreMax?: number;
  subjectId?: string;
  page?: number;
  pageSize?: number;
}

/** 监护人输入（新建/编辑）。 */
export interface GuardianInput {
  name: string;
  relationship: GuardianRelationship;
}

/** 新建学生请求体。 */
export interface CreateStudent {
  name: string;
  gender: StudentGender;
  gradeStage: GradeStage;
  homeCampusId: string;
  subjectIds?: string[];
  guardians: GuardianInput[];
  guardianMobile: string;
  tags?: string[];
}

/** 编辑学生请求体（全 optional，只读字段服务端忽略）。 */
export interface UpdateStudent {
  name?: string;
  gender?: StudentGender;
  gradeStage?: GradeStage;
  homeCampusId?: string;
  subjectIds?: string[];
  guardians?: GuardianInput[];
  guardianMobile?: string;
  tags?: string[];
}

/** CSV 导入预览行。 */
export interface StudentImportRow {
  rowNumber: number;
  name?: string;
  gender?: string;
  gradeStage?: string;
  campusName?: string;
  guardianMobile?: string;
  tags?: string;
  /** 科目分数解析结果（「科目:分数;…」逐科解析，留空不录入）。 */
  subjectScores?: SubjectScore[];
  errors: string[];
  valid: boolean;
}

/** CSV 导入预览响应。 */
export interface StudentImportPreview {
  rows: StudentImportRow[];
  validCount: number;
  errorCount: number;
}

/** CSV 导入结果。 */
export interface StudentImportResult {
  imported: number;
  skipped: number;
}

function buildListQuery(query: StudentListQuery): string {
  const params = new URLSearchParams();
  if (query.keyword) params.set('keyword', query.keyword);
  if (query.campus) params.set('campus', query.campus);
  if (query.status) params.set('status', query.status);
  if (query.gradeStage) params.set('gradeStage', query.gradeStage);
  if (query.gender) params.set('gender', query.gender);
  if (query.scoreMin !== undefined) params.set('scoreMin', String(query.scoreMin));
  if (query.scoreMax !== undefined) params.set('scoreMax', String(query.scoreMax));
  if (query.subjectId) params.set('subjectId', query.subjectId);
  if (query.page) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  const qs = params.toString();
  return qs ? `${ApiPaths.students.list}?${qs}` : ApiPaths.students.list;
}

async function listStudents(
  query: StudentListQuery = {},
): Promise<Paginated<StudentListItem>> {
  return http.get<Paginated<StudentListItem>>(buildListQuery(query));
}

async function getStudent(id: string): Promise<StudentDetail> {
  return http.get<StudentDetail>(ApiPaths.students.detail(id));
}

async function createStudent(dto: CreateStudent): Promise<StudentDetail> {
  return http.post<StudentDetail>(ApiPaths.students.create, dto);
}

async function updateStudent(
  id: string,
  dto: UpdateStudent,
): Promise<StudentDetail> {
  return http.patch<StudentDetail>(ApiPaths.students.update(id), dto);
}

async function archiveStudent(id: string): Promise<{ id: string; status: string }> {
  return http.post<{ id: string; status: string }>(
    ApiPaths.students.archive(id),
    {},
  );
}

async function restoreStudent(
  id: string,
): Promise<{ id: string; status: string }> {
  return http.post<{ id: string; status: string }>(
    ApiPaths.students.restore(id),
    {},
  );
}

async function previewImport(csv: string): Promise<StudentImportPreview> {
  return http.post<StudentImportPreview>(ApiPaths.students.importPreview, { csv });
}

async function confirmImport(csv: string): Promise<StudentImportResult> {
  return http.post<StudentImportResult>(ApiPaths.students.importConfirm, { csv });
}

async function searchStudents(keyword: string): Promise<StudentSearchItem[]> {
  const qs = keyword ? `?keyword=${encodeURIComponent(keyword)}` : '';
  return http.get<StudentSearchItem[]>(`${ApiPaths.students.search}${qs}`);
}

async function viewMobilePlaintext(id: string): Promise<string> {
  return http.get<string>(ApiPaths.students.mobilePlaintext(id));
}

/** 学生档案 API 唯一对外入口。 */
export const studentsApi = {
  listStudents,
  getStudent,
  createStudent,
  updateStudent,
  archiveStudent,
  restoreStudent,
  previewImport,
  confirmImport,
  searchStudents,
  viewMobilePlaintext,
};
